/**
 * La salida del editor cuando el dashboard rebota por aquí.
 *
 * ── Por qué esto no es sólo `await signOut()` ───────────────────────────────
 * Lo era, y dejaba la pantalla colgada para siempre. `supabase.auth.signOut()`
 * empieza por `await this.initializePromise` —antes de tomar su candado y sin
 * plazo ninguno—, y esa promesa puede no resolverse nunca: `_initialize` puede
 * estar esperando un refresco de token cuyo `fetch` no lleva timeout, o el
 * candado `navigator.locks` que comparten las pestañas del mismo origen. Ese
 * candado sólo existe en Chromium, que es donde se vio el fallo; en los demás
 * navegadores la librería cae a un candado de mentira y el cuelgue no aparece.
 *
 * Si el `await` no vuelve, el `location.replace` de después no se ejecuta y el
 * navegador se queda en `?signout=1` sin nada que hacer.
 *
 * ── La regla ────────────────────────────────────────────────────────────────
 * Salir no puede depender de que una promesa conteste. El borrado de verdad lo
 * hace `clearStoredSession()`, que es síncrono; `signOut()` se lanza igual —hace
 * más cosas que borrar la llave— pero con plazo, y se redirige pase lo que pase.
 */
import { clearStoredSession, supabase } from './lib/supabase'
import { clearIdentityRetries, dashboardReturnUrl } from './appUtils'

/** Lo que se le da a `signOut` antes de seguir sin él. */
export const SIGNOUT_GRACE_MS = 1_500

export type SignOutDeps = {
  signOut: () => Promise<unknown>
  clearStored: () => void
  clearRetries: () => void
  redirect: (url: string) => void
  /** Inyectable para que la prueba no espere de verdad. */
  delay: (ms: number) => Promise<void>
}

const defaults = (): SignOutDeps => ({
  signOut: () => supabase.auth.signOut({ scope: 'local' }),
  clearStored: clearStoredSession,
  clearRetries: clearIdentityRetries,
  redirect: (url) => { window.location.replace(url) },
  delay: (ms) => new Promise((resolve) => { setTimeout(resolve, ms) }),
})

export async function runSignOut(returnTo: string | null, deps?: Partial<SignOutDeps>): Promise<void> {
  const { signOut, clearStored, clearRetries, redirect, delay } = { ...defaults(), ...deps }

  /* Primero lo que no puede fallar. Si el resto se cuelga, la sesión ya no
     está: es lo único que esta pantalla tiene que garantizar. */
  clearStored()
  clearRetries()

  /* Y luego `signOut`, con plazo. `catch` porque una sesión que ya no existe
     hace que devuelva error, y eso aquí no es un problema: es el objetivo. */
  await Promise.race([
    signOut().catch(() => undefined),
    delay(SIGNOUT_GRACE_MS),
  ])

  redirect(dashboardReturnUrl(returnTo))
}
